import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Operation.css";
import { Loading } from "../loading/Loading";
import OperationCard from "./operationCard/OperationCard";
import { APP_COLORS } from "../../utils/helpers";
import { integrationLabel, normalizeIntegrationKey } from "../../utils/integrations";
import { fetchMenusAPI, fetchStoresAPI } from "../../utils/api/operations";
import { Context } from "../../context/Context";
import { useRestaurantAccess } from "../../access/hooks/useRestaurantAccess";
import { useOperationalAccess } from "../../access/hooks/useOperationalAccess";
import SubscriptionLockNotice from "../subscription/SubscriptionLockNotice";
import FilterGroup from "../analysis/filter/FilterUI";
import { translations } from "../../utils/lang";
import {
  fetchActionsfullSummaryAPI,
  fetchActionsAPI,
  deleteActionAPI,
} from "../../utils/api/actions";
import StatusHero from "./hero/StatusHero";
import CampaignActionsMenu from "./campaignActions/CampaignActionsMenu";
import StatusBadge from "./campaignActions/StatusBadge";
import ActionWorkflowButtons from "./campaignActions/ActionWorkflowButtons";
import { DeleteModal } from "../modals/Delete";
import {
  Button,
  Input,
  NoticeBox,
  CreditCard,
  Table,
  Table2,
  Trash2,
  ExpandableText,
} from "../ui";
import {
  actionTypeLabel,
  getActionDate,
  getDefaultFilters,
  parseMonthInput,
} from "./operationUtils";

// hints of the folded long text (Operations table)
const EXPAND_LABELS = {
  ar: { expand: "اضغط لعرض النص كاملاً", collapse: "اضغط لطيّ النص" },
  en: { expand: "Click to show the full text", collapse: "Click to collapse the text" },
};

const formatDate = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseDateInput = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

// App filter values are canonical keys ("LUD" = the all-apps card).
const APPS = ["jahez", "hungerstation", "marsool", "toyou", "keeta", "ninja", "LUD"];
const appKeyOf = (value) => normalizeIntegrationKey(value) || value;

// Each card loads several reports, so the dashboard shows at most this many
// store/app pairs; filters narrow the selection.
const MAX_STORE_APPS = 12;

const VIEW_MODES = {
  CARDS: "cards",
  TABLE: "table",
};

// "View in Operations" from the Campaigns page opens ?actions=<ids>&stores=<ids>.
const readFocus = () => {
  if (typeof window === "undefined") return { ids: [], stores: [] };
  const params = new URLSearchParams(window.location.search);
  const list = (key) => (params.get(key) || "").split(",").filter(Boolean);
  return { ids: list("actions"), stores: list("stores") };
};

const OPERATION_TABLE_TEXT = {
  ar: {
    cardsView: "عرض البطاقات",
    tableView: "عرض الجدول",
    store: "المتجر",
    app: "التطبيق",
    id: "المعرف",
    type: "نوع الإجراء",
    content: "محتوى الإجراء",
    status: "الحالة",
    date: "التاريخ",
    loading: "جاري تحميل الإجراءات...",
    empty: "لا توجد إجراءات للعرض",
    actions: "الإجراءات",
    delete: "حذف",
    month: "الشهر",
    from: "من",
    to: "إلى",
    storeFilter: "المتجر",
    appsFilter: "التطبيقات",
    viewMode: "طريقة العرض",
    storesLoadError: "تعذر تحميل المتاجر",
    summaryError: "تعذر تحميل ملخص حالات الإجراءات",
    tableLoadError: (failed, total) =>
      `تعذر تحميل إجراءات ${failed} من ${total} متجر/تطبيق. قد يكون الجدول غير مكتمل.`,
    statusError: "تعذر تحديث حالة الإجراء. حاول مرة أخرى.",
    deleteError: "تعذر حذف الإجراء. حاول مرة أخرى.",
    limitNotice: (shown, total) =>
      `يتم عرض ${shown} من ${total} متجر/تطبيق. استخدم الفلاتر لعرض الباقي.`,
    retry: "إعادة المحاولة",
    focusNotice: (n) => `يتم عرض ${n} طلب أُرسل من صفحة الحملات.`,
    showAll: "عرض الكل",
  },
  en: {
    cardsView: "Cards View",
    tableView: "Table View",
    store: "Store",
    app: "App",
    id: "ID",
    type: "Action Type",
    content: "Action Content",
    status: "Status",
    date: "Date",
    loading: "Loading actions...",
    empty: "No actions to display",
    actions: "Actions",
    delete: "Delete",
    month: "Month",
    from: "From",
    to: "To",
    storeFilter: "Store",
    appsFilter: "Apps",
    viewMode: "View mode",
    storesLoadError: "Stores could not be loaded",
    summaryError: "The action status summary could not be loaded",
    tableLoadError: (failed, total) =>
      `Actions for ${failed} of ${total} store/app pairs could not be loaded. The table may be incomplete.`,
    statusError: "The action status could not be updated. Please try again.",
    deleteError: "The action could not be deleted. Please try again.",
    limitNotice: (shown, total) =>
      `Showing ${shown} of ${total} store/app pairs. Use the filters to see the rest.`,
    retry: "Try again",
    focusNotice: (n) => `Showing ${n} request${n === 1 ? "" : "s"} submitted from Campaigns.`,
    showAll: "Show all",
  },
};

export default function Operation() {
  const token = localStorage.getItem("auth-token");
  const {
    lang,
    storeAccess,
    handleTokenExpiration,
  } = useContext(Context);
  const { has, canFor } = useRestaurantAccess();

  const tableText = OPERATION_TABLE_TEXT[lang] || OPERATION_TABLE_TEXT.ar;
  const expandLabels = EXPAND_LABELS[lang] || EXPAND_LABELS.ar;
  const canAdminActions = has("operation.action.admin");

  const [menuCount, setMenuCount] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [summaryError, setSummaryError] = useState(false);
  // Mutations refresh only the cards of the affected store (`byStore`);
  // bulk creation refreshes everything (`all`).
  const [refreshTick, setRefreshTick] = useState({ all: 0, byStore: {} });
  const [focus, setFocus] = useState(readFocus);
  const [viewMode, setViewMode] = useState(() =>
    readFocus().ids.length ? VIEW_MODES.TABLE : VIEW_MODES.CARDS,
  );
  const focusActive = focus.ids.length > 0;
  const clearFocus = () => {
    setFocus({ ids: [], stores: [] });
    window.history.replaceState(null, "", window.location.pathname);
  };
  const [actionRows, setActionRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableFailures, setTableFailures] = useState(0);
  const [deleteAction, setDeleteAction] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storesError, setStoresError] = useState(false);
  // The server decides what each role may do. Campaign actions are operational: the role must hold them AND at least one of the client's
  // stores must have an active subscription; a `new` client (or a store whose subscription ended) sees the box locked and explained.
  const operational = useOperationalAccess(stores.map((store) => store.id));
  const campaignAccess = operational.check("operation.campaign.use", undefined, { ignoreScope: true });
  const canUseCampaignActions = campaignAccess.allowed;
  const [filters, setFilters] = useState(getDefaultFilters);

  // The context recreates these on every provider render (storeAccess falls
  // back to a new []), so read them through a ref inside fetchers.
  const contextRef = useRef({ handleTokenExpiration, storeAccess });
  contextRef.current = { handleTokenExpiration, storeAccess };

  const refreshStore = useCallback((storeId) => {
    setRefreshTick((prev) => ({
      ...prev,
      byStore: { ...prev.byStore, [storeId]: (prev.byStore[storeId] || 0) + 1 },
    }));
  }, []);

  const refreshAll = useCallback(() => {
    setRefreshTick((prev) => ({ ...prev, all: prev.all + 1 }));
  }, []);

  // Changes whenever any refresh happens; drives the summary and table.
  const anyRefresh = useMemo(
    () =>
      refreshTick.all +
      Object.values(refreshTick.byStore).reduce((sum, n) => sum + n, 0),
    [refreshTick],
  );

  const allStores = useMemo(() => {
    return [...new Set(stores.map((s) => s.storeName?.toLowerCase()))];
  }, [stores]);

  const allStoresIds = useMemo(() => {
    return [...new Set(stores.map((s) => s.id?.toLowerCase()))];
  }, [stores]);

  const handleFilterChange = (key, values) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const handleMonthChange = (value) => {
    // Ignore a cleared input rather than sending an invalid month.
    const parsed = parseMonthInput(value);
    if (!parsed) return;
    setFilters((prev) => ({ ...prev, MonthlyDate: parsed }));
  };

  const fetchStores = useCallback(async () => {
    if (!token) {
      contextRef.current.handleTokenExpiration();
      return;
    }
    setStoresError(false);
    try {
      // The helper resolves to undefined on failure.
      const storesData = await fetchStoresAPI(token, contextRef.current.storeAccess);
      if (storesData) setStores(storesData);
      else setStoresError(true);
    } catch (err) {
      console.error(err);
      setStoresError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Menu counts are not store-scoped, so they are fetched once.
  useEffect(() => {
    if (!token) return;
    let active = true;
    fetchMenusAPI(token)
      .then((data) => {
        if (active) setMenuCount(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error("Menu counts fetch failed", err));
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !allStoresIds.length) return undefined;
    let active = true;

    fetchActionsfullSummaryAPI(token, allStoresIds).then((data) => {
      if (!active) return;
      setSummaryError(!data);
      setStatuses(Array.isArray(data) ? data : []);
    });

    return () => {
      active = false;
    };
  }, [token, allStoresIds, anyRefresh]);

  const filteredStatuses = useMemo(() => {
    const selectedStoreIds = filters.store.length
      ? stores
          .filter((store) =>
            filters.store.includes(store.storeName?.toLowerCase()),
          )
          .map((store) => store.id)
      : [];

    return statuses.filter((s) => {
      const storeMatch =
        selectedStoreIds.length === 0 || selectedStoreIds.includes(s.storeId);
      const appMatch =
        filters.app.length === 0 ||
        filters.app.includes(appKeyOf(s.appName));

      return storeMatch && appMatch;
    });
  }, [statuses, filters.store, filters.app, stores]);

  // Every store/app pair matching the store/app filters. Dates are not part
  // of this, so changing them doesn't refetch the table.
  const matchingStoreApps = useMemo(() => {
    const combinations = [];
    const seenKeys = new Map();

    for (const store of stores) {
      if (focusActive && focus.stores.length && !focus.stores.includes(store.id)) continue;
      if (
        filters.store.length > 0 &&
        !filters.store.includes(store.storeName?.toLowerCase())
      )
        continue;

      const apps = store.apps || [];
      const filteredApps =
        filters.app.length === 0
          ? apps
          : apps.filter((app) => filters.app.includes(appKeyOf(app.app)));

      for (const app of filteredApps) {
        // Stable per pair (so cards aren't remounted and refetched when other
        // filters change), with a suffix if the data ever repeats a pair.
        const baseKey = `${store.id}-${app.id}-${app.app}`;
        const seen = seenKeys.get(baseKey) || 0;
        seenKeys.set(baseKey, seen + 1);
        combinations.push({ store, app, key: seen ? `${baseKey}-${seen}` : baseKey });
      }
    }

    return combinations;
  }, [stores, filters.store, filters.app, focusActive, focus.stores]);

  const limitedStoreApps = useMemo(
    () => matchingStoreApps.slice(0, MAX_STORE_APPS),
    [matchingStoreApps],
  );

  useEffect(() => {
    if (viewMode !== VIEW_MODES.TABLE) return undefined;
    let isMounted = true;

    const fetchTableActions = async () => {
      if (!token) {
        contextRef.current.handleTokenExpiration();
        return;
      }

      if (!limitedStoreApps.length) {
        setActionRows([]);
        setTableFailures(0);
        return;
      }

      setTableLoading(true);

      try {
        let failures = 0;
        const results = await Promise.all(
          limitedStoreApps.map(async ({ store, app }) => {
            const actionsData = await fetchActionsAPI(token, {
              storeId: store.id,
              appName: app.app,
            });
            // The helper resolves to undefined when the request failed.
            if (!actionsData) failures += 1;

            return (actionsData || []).map((action) => ({
              ...action,
              storeName: store.storeName,
              storeId: store.id,
              appId: app.id,
              appName: action.appName || app.app,
            }));
          }),
        );

        if (isMounted) {
          setTableFailures(failures);
          setActionRows(
            results
              .flat()
              .sort(
                (a, b) =>
                  new Date(getActionDate(b)) - new Date(getActionDate(a)),
              ),
          );
        }
      } catch (err) {
        console.error("Actions table fetch failed", err);
      } finally {
        if (isMounted) {
          setTableLoading(false);
        }
      }
    };

    fetchTableActions();

    return () => {
      isMounted = false;
    };
  }, [token, viewMode, limitedStoreApps, anyRefresh]);

  const clearFilters = () => setFilters(getDefaultFilters());

  const handleDeleteAction = async (actionId) => {
    if (!token) {
      handleTokenExpiration();
      return;
    }

    // handler-level authorization, against the action's restaurant
    if (!canFor("operation.action.admin", deleteAction?.storeId)) return;
    setDeleteError("");
    const target = deleteAction;
    const deletedAction = await deleteActionAPI(token, actionId);

    if (deletedAction) {
      setActionRows((prev) => prev.filter((row) => row._id !== actionId));
      if (target?.storeId) refreshStore(target.storeId);
      else refreshAll();
      setDeleteAction(null);
    } else {
      // Keep the dialog open so the failure is visible and can be retried.
      setDeleteError(tableText.deleteError);
    }
  };

  const operationTableColumns = useMemo(
    () => [
      { key: "store", label: tableText.store, header: tableText.store },
      { key: "app", label: tableText.app, header: tableText.app },
      { key: "type", label: tableText.type, header: tableText.type },
      { key: "content", label: tableText.content, header: tableText.content },
      { key: "status", label: tableText.status, header: tableText.status },
      { key: "date", label: tableText.date, header: tableText.date },
      ...(canAdminActions
        ? [
            {
              key: "actions",
              label: tableText.actions,
              header: tableText.actions,
            },
          ]
        : []),
    ],
    [tableText, canAdminActions],
  );

  const operationTableData = useMemo(() => {
    const fromDate = filters.from ? new Date(filters.from) : null;
    const toDate = filters.to ? new Date(filters.to) : null;

    if (fromDate && !Number.isNaN(fromDate.getTime())) {
      fromDate.setHours(0, 0, 0, 0);
    }

    if (toDate && !Number.isNaN(toDate.getTime())) {
      toDate.setHours(23, 59, 59, 999);
    }

    // An action can come back under both its own app and the LUD view; keep
    // the app-specific copy (counted once instead of rescanning per row).
    const idCounts = new Map();
    actionRows.forEach((row) => {
      if (row._id) idCounts.set(row._id, (idCounts.get(row._id) || 0) + 1);
    });

    const filteredRows = actionRows.filter((action) => {
      if (focusActive) return focus.ids.includes(String(action._id));
      const actionDateValue = getActionDate(action);
      const actionDate = actionDateValue ? new Date(actionDateValue) : null;

      const dateMatch =
        actionDate && !Number.isNaN(actionDate.getTime())
          ? (!fromDate || actionDate >= fromDate) &&
            (!toDate || actionDate <= toDate)
          : !fromDate && !toDate;

      if (!dateMatch) return false;
      if (!action._id || idCounts.get(action._id) === 1) return true;
      return action.appName !== "LUD";
    });

    return filteredRows.map((action, index) => {
      const appLabel = integrationLabel(action.appName, lang);
      const actionDate = getActionDate(action);

      return {
        // Table keys rows by `id`; a stable key keeps each row's dropdown
        // state attached to the right action.
        id: action._id || `${action.storeId}-${action.appName}-${index}`,
        store: (
          <div>
            <div className="operation-table-main-text">{action.storeName}</div>
            <span className="operation-table-sub-text">#{action.storeId}</span>
          </div>
        ),
        app: (
          <div>
            <div className="operation-table-main-text">{appLabel}</div>
            <span className="operation-table-sub-text">#{action.appId}</span>
          </div>
        ),
        type: actionTypeLabel(action.type, lang) || "-",
        content: (
          <div className="operation-table-content">
            <ExpandableText text={action.content || "-"} labels={expandLabels} />
          </div>
        ),
        // the status, and the next steps the workflow allows this user (the same buttons as the
        // action cards and the Dashboard); every step asks for a confirmation first
        status: (
          <div className="operation-table-status-control">
            <StatusBadge status={action.status || "pending"} />
            <ActionWorkflowButtons id={action._id} allowed={action.allowed} action={action} lang={lang} onDone={() => refreshStore(action.storeId)} />
          </div>
        ),
        date: actionDate
          ? new Date(actionDate).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")
          : "-",
        ...(canAdminActions
          ? {
              // Campaigns are removed through the Campaign Actions box; only
              // legacy (menu / general) rows can still be purged here.
              actions: action.type === "campaigns" ? null : (
                <div className="operation-table-actions">
                  <Button
                    type="button"
                    variant="danger"
                    appearance="soft"
                    size="sm"
                    rounded
                    title={tableText.delete}
                    aria-label={`${tableText.delete}: ${action.type || ""} ${
                      action.storeName || ""
                    }`.trim()}
                    icon={<Trash2 size={16} />}
                    onClick={() => {
                      setDeleteError("");
                      setDeleteAction(action);
                    }}
                  />
                </div>
              ),
            }
          : {}),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actionRows,
    filters.from,
    filters.to,
    focusActive,
    focus.ids,
    lang,
    canAdminActions,
    tableText,
    expandLabels,
  ]);

  if (loading) {
    return <Loading />;
  }

  const hiddenPairs = matchingStoreApps.length - limitedStoreApps.length;

  return (
    <div className="operation">

      {storesError && (
        <div role="alert" className="operation-notice">
          <NoticeBox variant="danger" title={tableText.storesLoadError}>
            <Button size="sm" appearance="soft" onClick={fetchStores}>
              {tableText.retry}
            </Button>
          </NoticeBox>
        </div>
      )}

      <div className="filters operation-filters">
        <label>
          {tableText.month}
          <Input
            type="month"
            value={`${filters.MonthlyDate?.year}-${String(
              filters.MonthlyDate?.month,
            ).padStart(2, "0")}`}
            onChange={(e) => handleMonthChange(e.target.value)}
            fullWidth={false}
          />
        </label>

        <label>
          {tableText.from}
          <Input
            type="date"
            value={formatDate(filters.from)}
            onChange={(e) =>
              handleFilterChange("from", parseDateInput(e.target.value))
            }
            fullWidth={false}
          />
        </label>

        <label>
          {tableText.to}
          <Input
            type="date"
            value={formatDate(filters.to)}
            onChange={(e) =>
              handleFilterChange("to", parseDateInput(e.target.value))
            }
            fullWidth={false}
          />
        </label>

        <FilterGroup
          label={tableText.storeFilter}
          options={allStores}
          selected={filters.store}
          onChange={(v) => handleFilterChange("store", v)}
        />

        <FilterGroup
          label={tableText.appsFilter}
          options={APPS}
          getLabel={(app) => integrationLabel(app, lang)}
          selected={filters.app}
          onChange={(v) => handleFilterChange("app", v)}
        />

        <Button
          type="button"
          variant="danger"
          appearance="soft"
          onClick={clearFilters}
        >
          {translations[lang].reports.details.inputTitles[6]}
        </Button>
      </div>

      {summaryError && (
        <p className="operation-inline-error" role="status">
          {tableText.summaryError}
        </p>
      )}
      <StatusHero statuses={filteredStatuses} />

      <div className="operation-view-switch">
        {canUseCampaignActions && stores.length > 0 && (
          <CampaignActionsMenu stores={stores} onChanged={refreshAll} />
        )}
        {campaignAccess.locked && (
          <SubscriptionLockNotice
            lang={lang}
            reason={campaignAccess.reason}
            title={lang === "ar" ? "إجراءات الحملات" : "Campaign actions"}
          />
        )}

        <div
          className="operation-view-toggle"
          role="group"
          aria-label={tableText.viewMode}
        >
        <Button
          type="button"
          variant="primary"
          appearance={viewMode === VIEW_MODES.CARDS ? "filled" : "soft"}
          size="sm"
          icon={<CreditCard size={16} />}
          rounded
          aria-pressed={viewMode === VIEW_MODES.CARDS}
          onClick={() => setViewMode(VIEW_MODES.CARDS)}
        >
          {tableText.cardsView}
        </Button>

        <Button
          type="button"
          variant="primary"
          appearance={viewMode === VIEW_MODES.TABLE ? "filled" : "soft"}
          size="sm"
          icon={<Table2 size={16} />}
          rounded
          aria-pressed={viewMode === VIEW_MODES.TABLE}
          onClick={() => setViewMode(VIEW_MODES.TABLE)}
        >
          {tableText.tableView}
        </Button>
        </div>
      </div>

      {focusActive && (
        <p className="operation-limit-notice" role="status">
          {tableText.focusNotice(focus.ids.length)}{" "}
          <Button type="button" size="sm" appearance="soft" onClick={clearFocus}>
            {tableText.showAll}
          </Button>
        </p>
      )}

      {hiddenPairs > 0 && (
        <p className="operation-limit-notice" role="status">
          {tableText.limitNotice(limitedStoreApps.length, matchingStoreApps.length)}
        </p>
      )}

      {viewMode === VIEW_MODES.CARDS ? (
        <div className="operation-grid">
          {limitedStoreApps.map(({ store, app, key }) => (
            <OperationCard
              key={key}
              store={store}
              menu={menuCount.find((m) => m.id === store.id)}
              app={app}
              color={APP_COLORS[app.app] || "var(--ui-border)"}
              filters={filters}
              refreshKey={`${refreshTick.all}-${refreshTick.byStore[store.id] || 0}`}
              onRefresh={refreshStore}
            />
          ))}
        </div>
      ) : (
        <div className="operation-table-view" aria-busy={tableLoading || undefined}>
          {tableFailures > 0 && !tableLoading && (
            <p className="operation-inline-error" role="status">
              {tableText.tableLoadError(tableFailures, limitedStoreApps.length)}
            </p>
          )}
          {tableLoading ? (
            <div className="operation-table-empty" role="status">
              {tableText.loading}
            </div>
          ) : (
            <Table
              columns={operationTableColumns}
              data={operationTableData}
              emptyText={tableText.empty}
            />
          )}
        </div>
      )}

      {deleteAction && (
        <DeleteModal
          id={deleteAction._id}
          error={deleteError}
          handleDelete={handleDeleteAction}
          cancel={() => {
            setDeleteAction(null);
            setDeleteError("");
          }}
        />
      )}
    </div>
  );
}
